import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { registerUser, loginUser } from '../utils/apiServices';
import { AuthContext } from '../contexts/AuthContext';
import { DEPARTAMENTOS, getMunicipios } from '../utils/colombiaData';

// ---------------------------------------------------------------------------
// Clases Tailwind reutilizables
// ---------------------------------------------------------------------------
const PAGE_WRAPPER =
  'min-h-screen bg-gradient-to-br from-[#f4f7fc] to-[#e8f0fe] py-10 px-5 flex items-center justify-center';

const CARD_ICON =
  'w-[100px] h-[100px] mx-auto mb-[30px] bg-gradient-to-br from-icetex to-[#0056b3] ' +
  'rounded-full flex items-center justify-center shadow-[0_10px_30px_rgba(0,63,129,0.3)]';

const FORM_LABEL =
  'flex items-center gap-2 text-[#151843] text-[0.95rem] font-bold mb-2.5 uppercase tracking-wide ' +
  '[&_svg]:text-icetex [&_svg]:flex-shrink-0';

const FORM_INPUT =
  'w-full px-5 py-4 border-2 border-[#e8f0fe] rounded-[15px] text-base transition-all duration-300 ' +
  'bg-[#f8fafc] focus:outline-none focus:border-icetex focus:bg-white ' +
  'focus:shadow-[0_0_0_4px_rgba(0,63,129,0.1)] placeholder:text-[#64748b]';

const FORM_SELECT = FORM_INPUT + ' cursor-pointer';

const SUBMIT_BTN =
  'w-full py-[18px] bg-gradient-to-br from-icetex to-[#0056b3] text-white border-0 rounded-full ' +
  'text-[1.05rem] font-bold uppercase tracking-wide cursor-pointer transition-all duration-300 ' +
  'shadow-[0_6px_20px_rgba(0,63,129,0.3)] mb-[15px] hover:-translate-y-0.5 ' +
  'hover:shadow-[0_8px_25px_rgba(0,63,129,0.4)] active:translate-y-0';

const TOGGLE_BTN =
  'btn-icetex-outline w-full py-4 bg-transparent text-icetex border-2 border-icetex rounded-full ' +
  'text-[0.95rem] font-semibold cursor-pointer transition-all duration-300 mb-5 ' +
  'hover:bg-[#003F81] hover:text-white hover:border-[#003F81] hover:-translate-y-0.5';

const ERROR_ALERT =
  'flex items-center gap-3 px-5 py-4 bg-[#fee] border-2 border-[#fcc] rounded-[15px] mb-[25px] ' +
  'animate-shake [&_svg]:text-[#c00] [&_svg]:flex-shrink-0';

const SECTION_TITLE =
  'text-[#003F81] text-[0.85rem] font-extrabold uppercase tracking-[0.12em] mb-4 ' +
  'pb-2 border-b-2 border-[#e8f0fe]';

const RADIO_OPTION =
  'flex items-center gap-3 px-4 py-3 border-2 border-[#e8f0fe] rounded-[12px] cursor-pointer ' +
  'transition-all duration-200 hover:border-icetex hover:bg-[#f0f7ff] ' +
  'has-[:checked]:border-icetex has-[:checked]:bg-[#e8f4ff]';

// ---------------------------------------------------------------------------
// Íconos SVG inline reutilizables
// ---------------------------------------------------------------------------
const IconDoc = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const IconUser = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const IconMail = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
  </svg>
);
const IconLock = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconPhone = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 11.1 19.79 19.79 0 0 1 1.61 2.48 2 2 0 0 1 3.6.3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 7.91a16 16 0 0 0 6.1 6.1l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const IconMap = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);
const IconCalendar = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const IconAlert = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ---------------------------------------------------------------------------
// Validaciones del lado del cliente
// ---------------------------------------------------------------------------
function validateRegisterForm(fields) {
  const errors = {};
  const {
    document_type, document, full_name, date_of_birth,
    email, password, confirm_password,
    gender, phone_number, department, municipality,
    icetex_beneficiary, current_situation, support_area, solidarity_economy_priority, data_consent,
  } = fields;

  if (!document_type) errors.document_type = 'Seleccione el tipo de documento.';

  if (!document.trim()) {
    errors.document = 'Ingrese el número de documento.';
  } else if (['CC', 'TI'].includes(document_type) && !/^\d+$/.test(document.trim())) {
    errors.document = 'Para CC y TI el documento debe contener solo números.';
  } else if (document.trim().length < 5) {
    errors.document = 'El número de documento es demasiado corto.';
  }

  if (!full_name.trim()) errors.full_name = 'Ingrese su nombre completo.';

  if (!date_of_birth) {
    errors.date_of_birth = 'Ingrese su fecha de nacimiento.';
  } else {
    const dob = new Date(date_of_birth);
    const today = new Date();
    if (Number.isNaN(dob.getTime())) {
      errors.date_of_birth = 'La fecha de nacimiento no es válida.';
    } else if (dob > today) {
      errors.date_of_birth = 'La fecha de nacimiento no puede ser futura.';
    } else {
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
      if (age < 13) errors.date_of_birth = 'Debe tener al menos 13 años para registrarse.';
      else if (age > 120) errors.date_of_birth = 'Verifique su fecha de nacimiento.';
    }
  }

  if (!gender) errors.gender = 'Seleccione su género.';

  if (!email.trim()) errors.email = 'Ingrese su correo electr��nico.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'El correo electrónico no es válido.';

  const phone = phone_number.replace(/[\s-]/g, '');
  if (!phone) errors.phone_number = 'Ingrese su número de celular.';
  else if (!/^3\d{9}$/.test(phone)) errors.phone_number = 'Ingrese un celular colombiano válido (10 dígitos, iniciando con 3).';

  if (!department) errors.department = 'Seleccione el departamento.';
  if (!municipality) errors.municipality = 'Seleccione el municipio.';

  if (!password) {
    errors.password = 'Ingrese una contraseña.';
  } else if (
    password.length < 8 ||
    !/[A-Z]/.test(password) ||
    !/[0-9]/.test(password) ||
    !/[^A-Za-z0-9]/.test(password)
  ) {
    errors.password = 'La contraseña debe tener mínimo 8 caracteres, una mayúscula, un número y un símbolo.';
  }

  if (!confirm_password) errors.confirm_password = 'Confirme su contraseña.';
  else if (password !== confirm_password) errors.confirm_password = 'Las contraseñas no coinciden.';

  if (icetex_beneficiary === '') errors.icetex_beneficiary = 'Indique si es beneficiario de ICETEX.';

  if (!current_situation) errors.current_situation = 'Seleccione su situación actual.';
  if (!support_area) errors.support_area = 'Seleccione el área en la que necesita apoyo.';
  if (!solidarity_economy_priority) errors.solidarity_economy_priority = 'Seleccione una opción.';

  if (!data_consent) errors.data_consent = 'Debe aceptar el tratamiento de datos personales para registrarse.';

  return errors;
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
function AuthForm() {
  const [isRegister, setIsRegister] = useState(false);

  // Login state
  const [loginDoc, setLoginDoc] = useState('');
  const [loginPass, setLoginPass] = useState('');

  // Register state
  const [form, setForm] = useState({
    document_type: '',
    document: '',
    full_name: '',
    date_of_birth: '',
    gender: '',
    email: '',
    phone_number: '',
    department: '',
    municipality: '',
    password: '',
    confirm_password: '',
    icetex_beneficiary: '',
    current_situation: '',
    support_area: '',
    solidarity_economy_priority: '',
    data_consent: false,
  });

  const [errors, setErrors] = useState({});
  const { user, login, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  const municipios = form.department ? getMunicipios(form.department) : [];

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setForm((prev) => {
      const updated = { ...prev, [name]: newValue };
      // Reset municipio si cambia el departamento
      if (name === 'department') updated.municipality = '';
      return updated;
    });
    // Limpiar error del campo al editarlo
    if (errors[name]) setErrors((prev) => { const e = { ...prev }; delete e[name]; return e; });
  };

  // ---- Login ----
  const handleLogin = async () => {
    try {
      localStorage.removeItem('access');
      localStorage.removeItem('refresh');
      const data = await loginUser(loginDoc, loginPass);
      await login(data.access);
      setErrors({});
    } catch (error) {
      setErrors({ detail: error.response?.data?.detail || 'Error al iniciar sesión.' });
    }
  };

  // ---- Registro ----
  const handleRegister = async () => {
    const validationErrors = validateRegisterForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll al primer error
      setTimeout(() => {
        const el = document.querySelector('[data-error="true"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    try {
      const payload = {
        ...form,
        icetex_beneficiary: form.icetex_beneficiary === 'true',
      };
      const data = await registerUser(payload);
      await login(data.access);
      setErrors({});
    } catch (error) {
      const resp = error.response?.data;
      if (resp?.field) {
        setErrors({ [resp.field]: resp.detail });
      } else {
        setErrors({ detail: resp?.detail || 'Error al registrar. Revise los datos e intente de nuevo.' });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isRegister) handleRegister();
    else handleLogin();
  };

  const switchMode = () => {
    setIsRegister(!isRegister);
    setErrors({});
    setForm((prev) => ({ ...prev, password: '', confirm_password: '' }));
  };

  // ---- Loading ----
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#f4f7fc] to-[#e8f0fe]">
        <div className="w-[60px] h-[60px] border-4 border-[#e8f0fe] border-t-icetex rounded-full animate-spin" />
        <p className="mt-5 text-[#555] text-[1.1rem] font-semibold">Cargando...</p>
      </div>
    );
  }

  // ---- Ya autenticado ----
  if (user) {
    return (
      <div className={PAGE_WRAPPER}>
        <div className="max-w-[800px] w-full bg-white rounded-[30px] px-[50px] py-[60px] sm:px-8 sm:py-10 shadow-[0_20px_60px_rgba(0,63,129,0.15)] text-center animate-fade-in">
          <div className={CARD_ICON}>
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" /><line x1="9" y1="15" x2="15" y2="15" />
            </svg>
          </div>
          <h1 className="text-[#151843] text-[2.2rem] font-extrabold mb-[15px]">¡Bienvenido a Cursos Virtuales ICETEX</h1>
          <p className="text-[#555] text-[1.05rem] leading-relaxed mb-10">
            Tu espacio para el bienestar, el plan y el propósito de vida.
          </p>
          <div className="bg-gradient-to-br from-[#f0f7ff] to-[#e3f2fd] border-l-4 border-icetex rounded-[15px] p-[25px_30px] mb-10 text-left">
            <h2 className="text-[1.3rem] font-bold text-icetex mb-3">¿Qué puedes hacer aquí?</h2>
            <p className="text-base leading-relaxed text-[#151843] m-0">
              Cursos Virtuales ICETEX te acompaña con herramientas de autodiagnóstico, formación y recursos de apoyo para fortalecer tu bienestar emocional y tu proyecto personal. Conócete mejor, recibe recomendaciones personalizadas y da los siguientes pasos en tu proyecto de vida.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            {[
              {
                step: '01',
                title: 'Autodiagnóstico',
                text: 'Conócete mejor con el Explorador de Propósito y Proyecto de Vida, el Mapa Personal de Bienestar Emocional y la Rueda de la Vida.',
                href: '/diagnosticos',
                external: false,
                label: 'Hacer un diagnóstico',
              },
              {
                step: '02',
                title: 'Formación',
                text: 'Accede a cursos y talleres de bienestar, proyecto de vida y desarrollo de habilidades en la plataforma de formación.',
                href: 'https://moodle.cursosvirtualesicetex.com/login/index.php',
                external: true,
                label: 'Ir a Formación',
              },
              {
                step: '03',
                title: 'Recursos y apoyo',
                text: 'Encuentra cartillas, guías descargables y líneas de ayuda para acompañar tu bienestar.',
                href: '/recursos',
                external: false,
                label: 'Ver recursos',
              },
            ].map(({ step, title, text, href, external, label }) => (
              external ? (
                <a
                  key={title}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#f8fafc] border-2 border-[#e8f0fe] rounded-[15px] px-5 py-[25px] text-center transition-all duration-300 hover:-translate-y-[3px] hover:border-icetex hover:shadow-[0_8px_20px_rgba(0,63,129,0.1)] no-underline block"
                >
                  <span className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-icetex to-[#0056b3] rounded-full flex items-center justify-center text-white text-lg font-extrabold">{step}</span>
                  <h3 className="text-[1.1rem] font-bold text-[#151843] mb-2">{title}</h3>
                  <p className="text-[0.9rem] leading-[1.5] text-[#555] mb-3">{text}</p>
                  <span className="text-icetex text-[0.85rem] font-bold">{label} →</span>
                </a>
              ) : (
                <Link
                  key={title}
                  to={href}
                  className="bg-[#f8fafc] border-2 border-[#e8f0fe] rounded-[15px] px-5 py-[25px] text-center transition-all duration-300 hover:-translate-y-[3px] hover:border-icetex hover:shadow-[0_8px_20px_rgba(0,63,129,0.1)] no-underline block"
                >
                  <span className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-icetex to-[#0056b3] rounded-full flex items-center justify-center text-white text-lg font-extrabold">{step}</span>
                  <h3 className="text-[1.1rem] font-bold text-[#151843] mb-2">{title}</h3>
                  <p className="text-[0.9rem] leading-[1.5] text-[#555] mb-3">{text}</p>
                  <span className="text-icetex text-[0.85rem] font-bold">{label} →</span>
                </Link>
              )
            ))}
          </div>
          <div className="mb-[30px]">
            <Link to="/diagnosticos" className="w-full py-[18px] px-5 bg-gradient-to-br from-icetex to-[#0056b3] text-white border-0 rounded-full text-[1.05rem] font-bold uppercase tracking-wide inline-block no-underline transition-all duration-300 shadow-[0_6px_20px_rgba(0,63,129,0.3)] hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(0,63,129,0.4)]">
              Ir a Diagnósticos
            </Link>
          </div>
          <div className="text-center text-[#555] text-[0.9rem] pt-5 mt-5 border-t border-[#e8f0fe]">
            Plataforma de Herramientas Digitales - ICETEX
          </div>
        </div>
      </div>
    );
  }

  // ---- Derivados de contraseña (solo registro) ----
  const passwordRequirements = [
    { key: 'length', label: 'Mínimo 8 caracteres', valid: form.password.length >= 8 },
    { key: 'uppercase', label: 'Una letra mayúscula', valid: /[A-Z]/.test(form.password) },
    { key: 'number', label: 'Un número', valid: /[0-9]/.test(form.password) },
    { key: 'symbol', label: 'Un símbolo (!@#$…)', valid: /[^A-Za-z0-9]/.test(form.password) },
  ];
  const passwordScore = passwordRequirements.filter((req) => req.valid).length;
  const passwordStrength = [
    { label: '', bar: 'w-0 bg-transparent', text: 'text-[#64748b]' },
    { label: 'Muy débil', bar: 'w-1/4 bg-red-500', text: 'text-red-600' },
    { label: 'Débil', bar: 'w-2/4 bg-orange-500', text: 'text-orange-700' },
    { label: 'Media', bar: 'w-3/4 bg-yellow-500', text: 'text-yellow-700' },
    { label: 'Fuerte', bar: 'w-full bg-green-500', text: 'text-green-700' },
  ][passwordScore];
  const passwordsMatch = form.confirm_password.length > 0 && form.password === form.confirm_password;

  // ---- Formulario ----
  const FieldError = ({ name }) =>
    errors[name] ? (
      <p data-error="true" className="text-[#c00] text-[0.82rem] mt-1.5 font-medium">{errors[name]}</p>
    ) : null;

  const inputClass = (name) =>
    FORM_INPUT + (errors[name] ? ' border-[#fcc] focus:border-[#c00] focus:shadow-[0_0_0_4px_rgba(204,0,0,0.08)]' : '');

  const selectClass = (name) =>
    FORM_SELECT + (errors[name] ? ' border-[#fcc] focus:border-[#c00] focus:shadow-[0_0_0_4px_rgba(204,0,0,0.08)]' : '');

  return (
    <div className={PAGE_WRAPPER}>
      <div className="w-full max-w-[680px] animate-fade-in">
        {/* Botón volver */}
        <div className="mb-[30px]">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 bg-transparent text-icetex border-0 px-5 py-3 rounded-full text-base font-semibold cursor-pointer transition-all duration-300 hover:bg-white hover:-translate-x-1 hover:shadow-[0_4px_15px_rgba(0,63,129,0.2)]" type="button">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            Volver al Inicio
          </button>
        </div>

        <div className="bg-white rounded-[30px] px-[50px] py-[60px] sm:px-8 sm:py-10 shadow-[0_20px_60px_rgba(0,63,129,0.15)] text-center">
          <div className={CARD_ICON}>
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <h1 className="text-[#151843] text-[2.2rem] sm:text-[1.8rem] font-extrabold mb-[15px]">
            {isRegister ? 'Crear Cuenta' : 'Iniciar Sesión'}
          </h1>
          <p className="text-[#555] text-[1.05rem] leading-relaxed mb-10">
            {isRegister
              ? 'Complete el formulario para acceder a todas las herramientas de ICETEX'
              : 'Acceda a su cuenta para continuar'}
          </p>

          <form onSubmit={handleSubmit} className="text-left" noValidate>

            {/* ================================================================
                MODO LOGIN
            ================================================================ */}
            {!isRegister && (
              <>
                <div className="mb-[25px]">
                  <label htmlFor="loginDoc" className={FORM_LABEL}><IconDoc />Documento de Identidad</label>
                  <input type="text" id="loginDoc" value={loginDoc} onChange={(e) => setLoginDoc(e.target.value)} required className={FORM_INPUT} placeholder="Ingrese su número de documento" />
                </div>
                <div className="mb-[25px]">
                  <label htmlFor="loginPass" className={FORM_LABEL}><IconLock />Contraseña</label>
                  <input type="password" id="loginPass" value={loginPass} onChange={(e) => setLoginPass(e.target.value)} required className={FORM_INPUT} placeholder="Ingrese su contraseña" />
                </div>
              </>
            )}

            {/* ================================================================
                MODO REGISTRO
            ================================================================ */}
            {isRegister && (
              <>
                {/* ---- SECCIÓN: IDENTIDAD ---- */}
                <p className={SECTION_TITLE}>Documento de identidad</p>

                <div className="mb-[25px]">
                  <label htmlFor="document_type" className={FORM_LABEL}><IconDoc />Tipo de documento</label>
                  <select id="document_type" name="document_type" value={form.document_type} onChange={handleFormChange} className={selectClass('document_type')}>
                    <option value="">Seleccione el tipo de documento</option>
                    <option value="CC">Cédula de Ciudadanía (CC)</option>
                    <option value="TI">Tarjeta de Identidad (TI)</option>
                    <option value="CE">Cédula de Extranjería (CE)</option>
                    <option value="PEP">Permiso Especial de Permanencia (PEP)</option>
                    <option value="OTRO">Otro</option>
                  </select>
                  <FieldError name="document_type" />
                </div>

                <div className="mb-[25px]">
                  <label htmlFor="document" className={FORM_LABEL}><IconDoc />Número de documento</label>
                  <input type="text" id="document" name="document" value={form.document} onChange={handleFormChange} className={inputClass('document')} placeholder="Ingrese su número de documento" />
                  <FieldError name="document" />
                </div>

                {/* ---- SECCIÓN: DATOS PERSONALES ---- */}
                <p className={SECTION_TITLE}>Datos personales</p>

                <div className="mb-[25px]">
                  <label htmlFor="full_name" className={FORM_LABEL}><IconUser />Nombres y apellidos</label>
                  <input type="text" id="full_name" name="full_name" value={form.full_name} onChange={handleFormChange} className={inputClass('full_name')} placeholder="Ingrese su nombre completo" />
                  <FieldError name="full_name" />
                </div>

                <div className="mb-[25px]">
                  <label htmlFor="date_of_birth" className={FORM_LABEL}><IconCalendar />Fecha de nacimiento</label>
                  <input type="date" id="date_of_birth" name="date_of_birth" value={form.date_of_birth} onChange={handleFormChange} className={inputClass('date_of_birth')} max={new Date().toISOString().split('T')[0]} />
                  <FieldError name="date_of_birth" />
                </div>

                <div className="mb-[25px]">
                  <label className={FORM_LABEL}><IconUser />Género</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[['F', 'Femenino'], ['M', 'Masculino'], ['O', 'Otro'], ['NR', 'Prefiero no decirlo']].map(([val, label]) => (
                      <label key={val} className={RADIO_OPTION}>
                        <input type="radio" name="gender" value={val} checked={form.gender === val} onChange={handleFormChange} className="accent-icetex w-4 h-4 flex-shrink-0" />
                        <span className="text-[0.9rem] font-medium text-[#151843]">{label}</span>
                      </label>
                    ))}
                  </div>
                  <FieldError name="gender" />
                </div>

                {/* ---- SECCIÓN: CONTACTO ---- */}
                <p className={SECTION_TITLE}>Información de contacto</p>

                <div className="mb-[25px]">
                  <label htmlFor="email" className={FORM_LABEL}><IconMail />Correo electrónico</label>
                  <input type="email" id="email" name="email" value={form.email} onChange={handleFormChange} className={inputClass('email')} placeholder="ejemplo@correo.com" />
                  <FieldError name="email" />
                </div>

                <div className="mb-[25px]">
                  <label htmlFor="phone_number" className={FORM_LABEL}><IconPhone />Número de teléfono móvil</label>
                  <input type="tel" id="phone_number" name="phone_number" value={form.phone_number} onChange={handleFormChange} className={inputClass('phone_number')} placeholder="3XX XXX XXXX" maxLength={10} />
                  <FieldError name="phone_number" />
                </div>

                {/* ---- SECCIÓN: UBICACIÓN ---- */}
                <p className={SECTION_TITLE}>Lugar de residencia</p>

                <div className="mb-[25px]">
                  <label htmlFor="department" className={FORM_LABEL}><IconMap />Departamento de residencia</label>
                  <select id="department" name="department" value={form.department} onChange={handleFormChange} className={selectClass('department')}>
                    <option value="">Seleccione el departamento</option>
                    {DEPARTAMENTOS.map((dep) => (
                      <option key={dep} value={dep}>{dep}</option>
                    ))}
                  </select>
                  <FieldError name="department" />
                </div>

                <div className="mb-[25px]">
                  <label htmlFor="municipality" className={FORM_LABEL}><IconMap />Ciudad / Municipio</label>
                  <select id="municipality" name="municipality" value={form.municipality} onChange={handleFormChange} disabled={!form.department} className={selectClass('municipality') + (!form.department ? ' opacity-50 cursor-not-allowed' : '')}>
                    <option value="">{form.department ? 'Seleccione el municipio' : 'Primero seleccione el departamento'}</option>
                    {municipios.map((mun) => (
                      <option key={mun} value={mun}>{mun}</option>
                    ))}
                  </select>
                  <FieldError name="municipality" />
                </div>

                {/* ---- SECCIÓN: CONTRASEÑA ---- */}
                <p className={SECTION_TITLE}>Credenciales de acceso</p>

                <div className="mb-[25px]">
                  <label htmlFor="password" className={FORM_LABEL}><IconLock />Contraseña</label>
                  <input type="password" id="password" name="password" value={form.password} onChange={handleFormChange} className={inputClass('password')} placeholder="Mínimo 8 caracteres" autoComplete="new-password" />

                  {form.password.length > 0 && (
                    <div className="mt-3">
                      {/* Barra de fortaleza */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-[6px] bg-[#e8f0fe] rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-300 ${passwordStrength.bar}`} />
                        </div>
                        <span className={`text-[0.8rem] font-bold whitespace-nowrap ${passwordStrength.text}`}>
                          {passwordStrength.label}
                        </span>
                      </div>

                      {/* Checklist de requisitos */}
                      <ul className="list-none p-0 m-0 mt-3 grid grid-cols-2 gap-x-4 gap-y-[6px] max-md:grid-cols-1">
                        {passwordRequirements.map((req) => (
                          <li key={req.key} className={`flex items-center gap-2 text-[0.82rem] font-semibold ${req.valid ? 'text-green-700' : 'text-[#64748b]'}`}>
                            {req.valid ? '✓' : '○'} {req.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <FieldError name="password" />
                </div>

                <div className="mb-[25px]">
                  <label htmlFor="confirm_password" className={FORM_LABEL}><IconLock />Confirmación de contraseña</label>
                  <input
                    type="password"
                    id="confirm_password"
                    name="confirm_password"
                    value={form.confirm_password}
                    onChange={handleFormChange}
                    autoComplete="new-password"
                    placeholder="Repita su contraseña"
                    className={
                      FORM_INPUT + ' border-2 ' + (
                        form.confirm_password.length === 0 ? 'border-[#e8f0fe]'
                        : passwordsMatch ? 'border-green-400'
                        : 'border-red-400'
                      )
                    }
                  />
                  {form.confirm_password.length > 0 && (
                    <p className={`mt-2 text-[0.82rem] font-semibold ${passwordsMatch ? 'text-green-700' : 'text-red-600'}`}>
                      {passwordsMatch ? '✓ Las contraseñas coinciden' : '✗ Las contraseñas no coinciden'}
                    </p>
                  )}
                  <FieldError name="confirm_password" />
                </div>

                {/* ---- SECCIÓN: ICETEX ---- */}
                <p className={SECTION_TITLE}>Vínculo con ICETEX</p>

                <div className="mb-[25px]">
                  <label className={FORM_LABEL}>¿Es beneficiario de ICETEX?</label>
                  <div className="flex gap-4">
                    {[['true', 'Sí'], ['false', 'No']].map(([val, label]) => (
                      <label key={val} className={RADIO_OPTION + ' flex-1 justify-center'}>
                        <input type="radio" name="icetex_beneficiary" value={val} checked={form.icetex_beneficiary === val} onChange={handleFormChange} className="accent-icetex w-4 h-4 flex-shrink-0" />
                        <span className="text-[0.9rem] font-medium text-[#151843]">{label}</span>
                      </label>
                    ))}
                  </div>
                  <FieldError name="icetex_beneficiary" />
                </div>

                {/* ---- SECCIÓN: CARACTERIZACIÓN ---- */}
                <p className={SECTION_TITLE}>Caracterización</p>

                <div className="mb-[25px]">
                  <label htmlFor="current_situation" className={FORM_LABEL}><IconUser />Para comenzar, ¿cuál es tu situación actual?</label>
                  <select id="current_situation" name="current_situation" value={form.current_situation} onChange={handleFormChange} className={selectClass('current_situation')}>
                    <option value="">Selecciona tu punto de partida</option>
                    <option value="ASPIRANTE">Aspirante — Estoy buscando dar inicio a mis estudios superiores</option>
                    <option value="ESTUDIANTE">Estudiante — Curso actualmente mi carrera (primeros semestres)</option>
                    <option value="FINALIZANDO">Finalizando — Estoy en la etapa final de mis estudios o próximo a graduarme</option>
                    <option value="GRADUADO">Graduado — Ya culminé mi formación profesional</option>
                    <option value="EMPLEADO">Empleado — Me encuentro trabajando actualmente</option>
                    <option value="EMPRENDEDOR_POTENCIA">Emprendedor en potencia — Tengo una idea de negocio que quiero validar</option>
                    <option value="EMPRENDEDOR_ACTIVO">Emprendedor activo — Ya tengo un emprendimiento en marcha</option>
                  </select>
                  <FieldError name="current_situation" />
                </div>

                <div className="mb-[25px]">
                  <label htmlFor="support_area" className={FORM_LABEL}><IconMap />¿En qué área te gustaría que te apoyáramos hoy?</label>
                  <select id="support_area" name="support_area" value={form.support_area} onChange={handleFormChange} className={selectClass('support_area')}>
                    <option value="">Selecciona el área de apoyo</option>
                    <option value="CRECIMIENTO_ACADEMICO">Crecimiento Académico — Quiero conocer opciones para continuar mi formación o mejorar mi perfil</option>
                    <option value="INSERCION_LABORAL">Inserción Laboral — Necesito herramientas para buscar y aplicar a mi primer o próximo empleo</option>
                    <option value="EVOLUCION_PROFESIONAL">Evolución Profesional — Busco nuevas metas o un cambio de rumbo en mi carrera actual</option>
                    <option value="DESARROLLO_IDEA">Desarrollo de Idea — Busco guía paso a paso para convertir mi idea en un proyecto real</option>
                    <option value="ESCALAMIENTO">Escalamiento — Necesito estrategias para hacer crecer y fortalecer mi negocio actual</option>
                  </select>
                  <FieldError name="support_area" />
                </div>

                <div className="mb-[25px]">
                  <label htmlFor="solidarity_economy_priority" className={FORM_LABEL}>
                    <IconUser />
                    Al terminar tus estudios, ¿qué tanta prioridad le darías a trabajar en proyectos sociales con organizaciones de economía solidaria (cooperativas, asociaciones, fundaciones entre otras) en comparación con un empleo tradicional?
                  </label>
                  <select id="solidarity_economy_priority" name="solidarity_economy_priority" value={form.solidarity_economy_priority} onChange={handleFormChange} className={selectClass('solidarity_economy_priority')}>
                    <option value="">Selecciona una opción</option>
                    <option value="PRIORIDAD_TOTAL">Prioridad total: Es mi meta principal; quiero emprender o unirme a una organización de este tipo.</option>
                    <option value="PRIORIDAD_MEDIA">Prioridad media: Lo veo como una buena alternativa, pero buscaré por igual en empresas privadas o públicas.</option>
                    <option value="PRIORIDAD_BAJA">Prioridad baja: Prefiero el empleo tradicional y solo miraría esta opción si no encuentro otra cosa.</option>
                    <option value="NINGUNA_PRIORIDAD">Ninguna prioridad: No me interesa este sector; prefiero enfocarme solo en empresas privadas o el gobierno.</option>
                    <option value="DESCONOCIMIENTO">Desconocimiento: No conozco cómo funciona este sector ni sabía que era una opción de empleo.</option>
                  </select>
                  <FieldError name="solidarity_economy_priority" />
                </div>

                {/* ---- SECCIÓN: CONSENTIMIENTO ---- */}
                <p className={SECTION_TITLE}>Protección de datos personales</p>

                <div className="mb-[30px]">
                  <label className={`flex gap-4 p-5 border-2 rounded-[15px] cursor-pointer transition-all duration-200 ${form.data_consent ? 'border-icetex bg-[#e8f4ff]' : errors.data_consent ? 'border-[#fcc] bg-[#fff8f8]' : 'border-[#e8f0fe] bg-[#f8fafc] hover:border-icetex hover:bg-[#f0f7ff]'}`}>
                    <input type="checkbox" name="data_consent" checked={form.data_consent} onChange={handleFormChange} className="accent-icetex w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="text-[0.9rem] leading-relaxed text-[#151843]">
                      Autorizo el tratamiento de mis datos personales de acuerdo con la{' '}
                      <span className="text-icetex font-semibold">política de privacidad</span>{' '}
                      de ICETEX y la Ley 1581 de 2012 (Habeas Data).
                      <span className="block mt-1 text-[#c00] text-[0.8rem] font-semibold">* Campo obligatorio para el registro</span>
                    </span>
                  </label>
                  <FieldError name="data_consent" />
                </div>
              </>
            )}

            {/* Error general */}
            {errors.detail && (
              <div className={ERROR_ALERT}>
                <IconAlert />
                <p className="text-[#c00] text-[0.95rem] font-semibold m-0">{errors.detail}</p>
              </div>
            )}

            <button type="submit" className={SUBMIT_BTN}>
              {isRegister ? 'Crear mi cuenta' : 'Iniciar Sesión'}
            </button>

            <button type="button" onClick={switchMode} className={TOGGLE_BTN}>
              {isRegister ? '¿Ya tiene cuenta? Inicie sesión' : '¿No tiene cuenta? Regístrese aquí'}
            </button>

            {!isRegister && (
              <Link to="/forgot-password" className="block text-center text-icetex text-[0.95rem] font-semibold no-underline transition-all duration-300 hover:text-[#0056b3] hover:underline">
                ¿Olvidó su contraseña?
              </Link>
            )}
          </form>
        </div>

        <div className="text-center mt-[30px] pt-5">
          <p className="text-[#555] text-[0.9rem]">Plataforma de Herramientas Digitales - ICETEX</p>
        </div>
      </div>
    </div>
  );
}

export default AuthForm;
